import { useId, useRef, useState } from 'react';

import '../styles/trend-chart.css';

// TSS vite/01 — common time-series curve (SVG area/line) for the portal's
// economic modules (liquidaciones, estado de cuenta, stats de finca…). Entity-
// agnostic: takes labels + series + a value formatter, renders smooth curves
// with a faint grid, an emphasized endpoint, a hover crosshair + tooltip, a
// legend (≥2 series), and a visually-hidden data table (a11y / dataviz).
//
// Series `variant`:
//  · 'area'      → data hue (teal), filled area + dots + endpoint label. For
//                  magnitude that's always ≥0 (p.ej. reparto mensual).
//  · 'context'   → muted dashed reference line (p.ej. la pensión detrás del reparto).
//  · 'diverging' → SIGNED value around a zero baseline (p.ej. el saldo del
//                  estado de cuenta): positivo (a pagar) sobre el cero en color
//                  de alerta, negativo (a favor) bajo el cero en verde. La
//                  posición respecto al cero es el desambiguador primario; el
//                  color es secundario (CVD-safe). Pasá `signLabels:{pos,neg}`
//                  para el matiz semántico en el tooltip.
// Colors live in trend-chart.css (design tokens).

const W = 640;
const PAD = { top: 18, right: 18, bottom: 26, left: 56 };

// Catmull-Rom → cubic bézier: smooth "curva" through the points.
function smoothPath(pts) {
    if (pts.length < 2) return '';
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += ` C ${c1x} ${c1y} ${c2x} ${c2y} ${p2[0]} ${p2[1]}`;
    }
    return d;
}

export default function TrendChart({
    xLabels = [],
    series = [],
    format = (v) => String(v),
    height = 190,
    ariaLabel,
}) {
    const svgRef = useRef(null);
    const uid = useId();
    const [hover, setHover] = useState(null);

    const n = xLabels.length;
    if (!n || !series.length) return null;

    const H = height;
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    // Domain includes 0 (baseline / zero line). Negatives push `lo` below 0.
    const allVals = series.flatMap((s) => s.values).filter((v) => v != null).map(Number);
    const dataMax = Math.max(0, ...allVals);
    const dataMin = Math.min(0, ...allVals);
    const span = (dataMax - dataMin) || 1;
    const hi = dataMax + span * 0.1;
    const lo = dataMin < 0 ? dataMin - span * 0.1 : 0;
    const hasNeg = dataMin < 0;

    const xAt = (i) => PAD.left + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const yAt = (v) => PAD.top + innerH * (1 - ((Number(v) || 0) - lo) / (hi - lo));
    const yZero = yAt(0);

    const ticks = hasNeg ? [hi, 0, lo] : [hi, (hi + lo) / 2, lo];
    const posClip = `${uid}-pos`;
    const negClip = `${uid}-neg`;

    const onMove = (e) => {
        const rect = svgRef.current.getBoundingClientRect();
        const rx = ((e.clientX - rect.left) / rect.width) * W;
        let i = Math.round(((rx - PAD.left) / innerW) * (n - 1));
        i = Math.max(0, Math.min(n - 1, i));
        setHover(i);
    };

    const hoverX = hover != null ? xAt(hover) : null;

    return (
        <figure className="trend-chart" aria-label={ariaLabel}>
            <svg
                ref={svgRef}
                className="trend-chart__svg"
                viewBox={`0 0 ${W} ${H}`}
                role="img"
                aria-label={ariaLabel}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
            >
                <defs>
                    <clipPath id={posClip}><rect x="0" y="0" width={W} height={Math.max(0, yZero)} /></clipPath>
                    <clipPath id={negClip}><rect x="0" y={yZero} width={W} height={Math.max(0, H - yZero)} /></clipPath>
                </defs>

                {/* grid + y labels; the zero line is emphasized when there are negatives */}
                {ticks.map((v, gi) => (
                    <g key={gi}>
                        <line
                            className={v === 0 && hasNeg ? 'trend-chart__zero' : 'trend-chart__grid'}
                            x1={PAD.left} y1={yAt(v)} x2={W - PAD.right} y2={yAt(v)}
                        />
                        <text className="trend-chart__axis-label" x={PAD.left - 8} y={yAt(v)} textAnchor="end" dominantBaseline="middle">
                            {format(v)}
                        </text>
                    </g>
                ))}

                {/* series */}
                {series.map((s) => {
                    const pts = s.values.map((v, i) => [xAt(i), yAt(v)]);
                    const line = smoothPath(pts);
                    const isArea = s.variant === 'area';
                    const isDiv = s.variant === 'diverging';
                    const lastVal = s.values[s.values.length - 1];
                    const areaD = (isArea || isDiv) && pts.length > 1
                        ? `${line} L ${pts[pts.length - 1][0]} ${yZero} L ${pts[0][0]} ${yZero} Z`
                        : '';
                    const endTone = isDiv ? (Number(lastVal) < 0 ? 'neg' : 'pos') : null;

                    return (
                        <g key={s.key}>
                            {isArea && areaD && <path className="trend-chart__area" d={areaD} />}
                            {isDiv && areaD && (
                                <>
                                    <path className="trend-chart__area trend-chart__area--pos" d={areaD} clipPath={`url(#${posClip})`} />
                                    <path className="trend-chart__area trend-chart__area--neg" d={areaD} clipPath={`url(#${negClip})`} />
                                </>
                            )}
                            <path className={`trend-chart__line trend-chart__line--${s.variant}`} d={line} />
                            {(isArea || isDiv) && pts.map((p, i) => (
                                <circle
                                    key={i}
                                    className={`trend-chart__dot trend-chart__dot--${s.variant} ${i === pts.length - 1 ? 'trend-chart__dot--last' : ''}`}
                                    cx={p[0]} cy={p[1]}
                                />
                            ))}
                            {(isArea || isDiv) && pts.length > 0 && (
                                <text
                                    className={`trend-chart__endpoint ${endTone ? `trend-chart__endpoint--${endTone}` : ''}`}
                                    x={pts[pts.length - 1][0]}
                                    y={pts[pts.length - 1][1] - 10}
                                    textAnchor="end"
                                >
                                    {format(lastVal)}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* hover crosshair + dots */}
                {hoverX != null && (
                    <g>
                        <line className="trend-chart__crosshair" x1={hoverX} y1={PAD.top} x2={hoverX} y2={H - PAD.bottom} />
                        {series.map((s) => (
                            <circle
                                key={s.key}
                                className={`trend-chart__hover-dot trend-chart__hover-dot--${s.variant}`}
                                cx={hoverX} cy={yAt(s.values[hover])}
                            />
                        ))}
                    </g>
                )}

                {/* x labels: first + last (avoid clutter) */}
                <text className="trend-chart__axis-label" x={xAt(0)} y={H - 8} textAnchor="start">{xLabels[0]}</text>
                {n > 1 && (
                    <text className="trend-chart__axis-label" x={xAt(n - 1)} y={H - 8} textAnchor="end">{xLabels[n - 1]}</text>
                )}
            </svg>

            {hover != null && (
                <div className="trend-chart__tooltip" style={{ left: `${(hoverX / W) * 100}%` }} role="presentation">
                    <span className="trend-chart__tooltip-period">{xLabels[hover]}</span>
                    {series.map((s) => {
                        const v = s.values[hover];
                        const hint = s.signLabels && v != null
                            ? (Number(v) < 0 ? s.signLabels.neg : s.signLabels.pos)
                            : null;
                        return (
                            <span key={s.key} className="trend-chart__tooltip-row">
                                <span className={`trend-chart__swatch trend-chart__swatch--${s.variant}`} aria-hidden="true" />
                                <span className="trend-chart__tooltip-label">
                                    {s.label}{hint ? ` · ${hint}` : ''}
                                </span>
                                <span className="trend-chart__tooltip-value">{format(v)}</span>
                            </span>
                        );
                    })}
                </div>
            )}

            {series.length > 1 && (
                <figcaption className="trend-chart__legend">
                    {series.map((s) => (
                        <span key={s.key} className="trend-chart__legend-item">
                            <span className={`trend-chart__swatch trend-chart__swatch--${s.variant}`} aria-hidden="true" />
                            {s.label}
                        </span>
                    ))}
                </figcaption>
            )}

            {/* a11y / dataviz: a table view always exists */}
            <table className="trend-chart__sr">
                <caption>{ariaLabel}</caption>
                <thead>
                    <tr>
                        <th scope="col">Período</th>
                        {series.map((s) => <th key={s.key} scope="col">{s.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {xLabels.map((label, i) => (
                        <tr key={label}>
                            <th scope="row">{label}</th>
                            {series.map((s) => <td key={s.key}>{format(s.values[i])}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    );
}
